/**
 * ScoreTimeline — dashboard chart showing the user's audit score
 * trajectory over time. Line chart with one point per completed audit
 * that has a score. Only renders when the user has 2+ scored audits
 * so a single-audit user isn't shown a lonely dot.
 *
 * Data source: the existing /api/history payload — each item has
 * ``overall_score`` (v2 audits or legacy) + ``created_at`` +
 * ``profile_name``. We render the timeline for the currently-selected
 * profile name (default: the user's own most-recent audit).
 */
import React, { useMemo, useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  ReferenceLine,
} from 'recharts';
import { TrendingUp, TrendingDown, Minus, ChevronDown, Trophy } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '../ui/dropdown-menu';
import { readProgress } from '../../lib/rewriteProgress';

function fmtDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch { return ''; }
}

function bandColor(score) {
  if (score == null) return 'hsl(220 8% 55%)';
  if (score >= 85) return 'rgb(56 189 248)';
  if (score >= 70) return 'rgb(16 185 129)';
  if (score >= 50) return 'rgb(245 158 11)';
  return 'rgb(239 68 68)';
}

function CustomTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="rounded-lg border border-border bg-card p-2.5 shadow-md text-[12px]">
      <p className="font-semibold" data-testid="score-timeline-tooltip-score">{p.score} / 100</p>
      <p className="text-muted-foreground">{fmtDate(p.created_at)}</p>
      {p.profile_name && <p className="text-muted-foreground text-[11px]">{p.profile_name}</p>}
      {p.rewriteDone && (
        <p className="mt-1 text-emerald-600 dark:text-emerald-400 font-medium flex items-center gap-1 text-[11px]">
          <Trophy size={11} /> Rewrite complete · 5 / 5 sections
        </p>
      )}
    </div>
  );
}

// Custom dot renderer — draws a filled gold circle with a trophy glow
// on any audit where the user completed all 5 rewrite sections.
// Regular audits get the default recharts dot.
function DotWithBadge(props) {
  const { cx, cy, payload } = props;
  if (cx == null || cy == null) return null;
  if (payload?.rewriteDone) {
    return (
      <g data-testid={`score-timeline-dot-done-${payload.id}`}>
        <circle cx={cx} cy={cy} r={10} fill="rgba(16,185,129,0.15)" />
        <circle cx={cx} cy={cy} r={6} fill="rgb(16,185,129)" stroke="white" strokeWidth={2} />
        {/* tiny trophy dot */}
        <circle cx={cx} cy={cy} r={1.6} fill="white" />
      </g>
    );
  }
  return <circle cx={cx} cy={cy} r={4} fill="white" stroke="url(#stg)" strokeWidth={2} />;
}

export default function ScoreTimeline({ history, ownAudits, currentUserId }) {
  // Group scored audits by profile_name so users tracking multiple
  // profiles (e.g. an agency admin) can flip between them.
  // Timeline includes any audit that has a computed score, even if the
  // downstream posts pipeline later failed — the deterministic score
  // itself is still valid and worth plotting for the user's progress.
  const scoredHistory = useMemo(
    () => (history || []).filter((h) => typeof h.overall_score === 'number'),
    [history],
  );

  // Default profile: the most recent scored audit that belongs to the
  // current user, else the most recent overall.
  const profiles = useMemo(() => {
    const seen = new Map();
    for (const h of scoredHistory) {
      const key = h.profile_name || 'Unknown profile';
      if (!seen.has(key)) seen.set(key, { name: key, count: 0, isOwn: !!(ownAudits ?? (h.user_id === currentUserId)) });
      seen.get(key).count += 1;
    }
    return Array.from(seen.values()).sort((a, b) => b.count - a.count);
  }, [scoredHistory, currentUserId, ownAudits]);

  const [selected, setSelected] = useState(() => (profiles[0]?.name || null));
  React.useEffect(() => {
    if (!selected && profiles.length) setSelected(profiles[0].name);
    // If the current selection disappears after a filter change, snap back.
    if (selected && !profiles.find((p) => p.name === selected)) {
      setSelected(profiles[0]?.name || null);
    }
  }, [profiles, selected]);

  const points = useMemo(() => {
    if (!selected) return [];
    return scoredHistory
      .filter((h) => (h.profile_name || 'Unknown profile') === selected)
      .slice()
      .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
      .map((h) => {
        // Read Rewrite progress from localStorage for THIS analysis so
        // we can annotate the trajectory with a trophy marker on any
        // audit where the user completed all 5 rewrite sections. This
        // is the visible causal link — did the work → next score jump.
        const prog = readProgress(h.id) || {};
        const doneCount = ['headline', 'about', 'experience', 'featured', 'skills']
          .filter((k) => !!prog[k]).length;
        return {
          id: h.id,
          score: h.overall_score,
          created_at: h.created_at,
          label: fmtDate(h.created_at),
          profile_name: h.profile_name,
          rewriteDone: doneCount === 5,
          rewriteDoneCount: doneCount,
        };
      });
  }, [scoredHistory, selected]);

  if (points.length < 2) return null;  // single-audit users skip the chart

  const first = points[0].score;
  const last = points[points.length - 1].score;
  const delta = last - first;
  const DeltaIcon = delta > 0 ? TrendingUp : delta < 0 ? TrendingDown : Minus;
  const deltaColor = delta > 0 ? 'text-emerald-600' : delta < 0 ? 'text-rose-600' : 'text-muted-foreground';
  const deltaLabel = delta === 0 ? 'no change' : `${delta > 0 ? '+' : ''}${delta} pts since first audit`;
  const currentBand = bandColor(last);

  return (
    <Card className="border-border" data-testid="score-timeline-card">
      <CardContent className="p-4 sm:p-5">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-[10px] font-mono-edge tracking-[0.22em] uppercase text-muted-foreground">Score trajectory</p>
              <Badge variant="secondary" className="text-[10px]" data-testid="score-timeline-count">{points.length} audits</Badge>
            </div>
            <p className="mt-0.5 text-sm">
              <span className="text-xl font-bold tabular-nums" style={{ color: currentBand }} data-testid="score-timeline-latest">{last}</span>
              <span className="text-muted-foreground">/100 · </span>
              <span className={`inline-flex items-center gap-1 text-[12px] font-medium ${deltaColor}`} data-testid="score-timeline-delta">
                <DeltaIcon size={12} /> {deltaLabel}
              </span>
            </p>
          </div>
          {profiles.length > 1 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="inline-flex items-center gap-1 rounded-full border border-border bg-card hover:bg-secondary px-2.5 py-1 text-[11.5px] font-medium transition-colors"
                  data-testid="score-timeline-profile-trigger"
                >
                  {selected || 'Choose profile'} <ChevronDown size={12} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                {profiles.map((p) => (
                  <DropdownMenuItem
                    key={p.name}
                    onSelect={(e) => { e.preventDefault(); setSelected(p.name); }}
                    className={`text-[13px] ${p.name === selected ? 'bg-primary/10 text-foreground' : ''}`}
                    data-testid={`score-timeline-profile-${p.name}`}
                  >
                    <span className="flex-1 truncate">{p.name}</span>
                    <span className="text-[10px] text-muted-foreground ml-2">{p.count}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        <div className="h-[180px] -ml-2" data-testid="score-timeline-chart">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={points} margin={{ top: 8, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="stg" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="rgb(239 68 68)" />
                  <stop offset="35%" stopColor="rgb(245 158 11)" />
                  <stop offset="70%" stopColor="rgb(16 185 129)" />
                  <stop offset="100%" stopColor="rgb(56 189 248)" />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="hsl(220 8% 88% / 0.4)" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis
                domain={[0, 100]}
                ticks={[0, 25, 50, 75, 100]}
                tick={{ fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                width={30}
              />
              {/* Score-band reference lines so users instantly see which
                  tier their trajectory is climbing into. */}
              <ReferenceLine y={70} stroke="hsl(220 8% 70%)" strokeDasharray="3 3" strokeOpacity={0.35} />
              <ReferenceLine y={50} stroke="hsl(220 8% 70%)" strokeDasharray="3 3" strokeOpacity={0.35} />
              <Tooltip content={<CustomTooltip />} />
              <Line
                type="monotone"
                dataKey="score"
                stroke="url(#stg)"
                strokeWidth={2.5}
                dot={<DotWithBadge />}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <p className="mt-2 text-[10.5px] text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-1">
          <span>Bands · red &lt; 50 · amber 50-69 · emerald 70-84 · sky 85+. Re-audit after applying fixes to see the lift.</span>
          {points.some((p) => p.rewriteDone) && (
            <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-400" data-testid="score-timeline-legend-rewrite">
              <Trophy size={11} /> Emerald dot = Rewrite 5 / 5 done before this audit
            </span>
          )}
        </p>
      </CardContent>
    </Card>
  );
}
